import { useState } from 'react'
import { HelpCircle } from 'lucide-react'
import { Modal } from '@/components/common'

interface LegendEntry {
  status: string
  swatch: string
  text: string
  title: string
  description: string
}

// Farb-Semantik laut /plans/colors-fix-v2.md §2. Swatch-Klassen entsprechen
// den Zeilen-/Schrift-Stilen aus lessonStatusStyles.ts (eine Quelle der Wahrheit
// wäre wünschenswert, aber hier bewusst explizit für die Legende aufgeführt).
const LEGEND_ENTRIES: LegendEntry[] = [
  {
    status: 'confirmed',
    swatch: 'bg-green-50 dark:bg-green-900/20',
    text: 'text-green-700 dark:text-green-400 font-bold',
    title: 'Übernommen',
    description: 'Lektion wurde diesem Lehrer definitiv übergeben.',
  },
  {
    status: 'could_cover',
    swatch: 'bg-gray-50 dark:bg-gray-800',
    text: 'text-gray-700 dark:text-gray-300',
    title: 'Könnte übernehmen',
    description: 'Reserve-Kandidat ist qualifiziert und verfügbar (noch nicht angefragt, nicht angenommen).',
  },
  {
    status: 'waiting',
    swatch: 'bg-blue-50 dark:bg-blue-900/20',
    text: 'text-blue-700 dark:text-blue-400',
    title: 'Interessiert / Wartepool',
    description: 'Lehrer hat provisorisches Interesse signalisiert und ist im Wartepool (noch nicht bestätigt).',
  },
  {
    status: 'declined',
    swatch: 'bg-red-50 dark:bg-red-900/20',
    text: 'text-red-700 dark:text-red-400',
    title: 'Abgelehnt / nicht interessiert',
    description: 'Lehrer hat die Lektion abgelehnt oder ist nicht interessiert.',
  },
  {
    status: 'not_available',
    swatch: 'bg-red-50 dark:bg-red-900/20',
    text: 'text-red-700 dark:text-red-400',
    title: 'Nicht verfügbar',
    description: 'Lehrer kann diese Lektion nicht übernehmen (nicht qualifiziert oder belegt).',
  },
  {
    status: 'pending_active',
    swatch: 'bg-orange-50 dark:bg-orange-900/20',
    text: 'text-orange-700 dark:text-orange-400',
    title: 'Angefragt, wartet auf Antwort',
    description: 'Lehrer wurde angefragt, das Antwortfenster läuft noch.',
  },
  {
    status: 'pending_timeout',
    swatch: 'bg-orange-50 dark:bg-orange-900/20',
    text: 'text-red-700 dark:text-red-400',
    title: 'Keine Antwort, kann noch annehmen',
    description: 'Keine Antwort (Antwortfenster abgelaufen, nächste Lehrperson wurde angefragt) – Annahme jederzeit noch möglich.',
  },
  {
    status: 'assigned_elsewhere',
    swatch: 'bg-gray-100 dark:bg-gray-700/40',
    text: 'text-gray-600 dark:text-gray-400',
    title: 'An andere Lehrperson vergeben',
    description: 'Diese Lektion wurde bereits einer anderen Lehrperson übergeben.',
  },
  {
    status: 'cancelled',
    swatch: 'bg-gray-200 dark:bg-gray-700/40',
    text: 'text-gray-500 dark:text-gray-400',
    title: 'Storniert',
    description: 'Lektion oder Anfrage wurde storniert.',
  },
]

interface CascadeLegendTriggerProps {
  /** Visuelles Label neben dem Fragezeichen (optional). */
  label?: string
  className?: string
}

/**
 * Kleines Fragezeichen-Icon, das auf Klick ein Modal mit der Farblegende der
 * Kaskaden-Lektions-Hover-Tabelle öffnet. Nur bei Klick sichtbar, sonst
 * unauffällig. Siehe colors-fix-v2 §5.5.
 */
export function CascadeLegendTrigger({ label, className = '' }: CascadeLegendTriggerProps) {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className={`inline-flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 align-middle ${className}`}
        aria-label="Farblegende der Kaskade anzeigen"
        title="Farblegende anzeigen"
      >
        {label ? <span>{label}</span> : null}
        <HelpCircle className="w-3.5 h-3.5" />
      </button>

      <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} title="Farblegende Kaskade" size="md">
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
          Bedeutung der Farben beim Hovern über eine Lehrperson in der Kaskade:
        </p>
        <ul className="space-y-2">
          {LEGEND_ENTRIES.map((entry) => (
            <li key={entry.status} className="flex items-start gap-3">
              <span
                className={`mt-0.5 inline-block w-24 shrink-0 rounded px-2 py-1 text-center text-xs ${entry.swatch} ${entry.text}`}
                aria-hidden="true"
              >
                Beispiel
              </span>
              <span className="text-sm">
                <span className="font-medium text-gray-900 dark:text-gray-100">{entry.title}</span>
                <span className="block text-xs text-gray-500 dark:text-gray-400">{entry.description}</span>
              </span>
            </li>
          ))}
        </ul>
      </Modal>
    </>
  )
}
